import {
  type PrescriptionRegistration,
  type PrescriptionRegistrationSort,
  type PrescriptionRegistrationSummary,
  type RegistrationStatus,
} from '@/entities/prescription-registration/api/prescription-registration.api';
import {
  usePrescriptionRegistrationList,
  usePrescriptionRegistrationMonths,
  usePrescriptionRegistrationSummary,
} from '@/entities/prescription-registration/hooks/usePrescriptionRegistrations';
import {
  buildRegistrationPeriodOptions,
  formatRegistrationPeriod,
  getCurrentRegistrationMonth,
  getRegistrationYearOptions,
  type RegistrationPeriodOption,
} from '@/entities/prescription-registration/registrationMonth';
import { RegistrationDetailPanel } from '@/entities/prescription-registration/ui/RegistrationDetailPanel';
import { RegistrationStatusBadge } from '@/entities/prescription-registration/ui/RegistrationStatusBadge';
import {
  DEFAULT_VIEW_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@/shared/constants/pagination';
import { formatCount, orDash, toSigungu } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import {
  Badge,
  Button,
  Card,
  DotSeparated,
  FilterButton,
  Icon,
  LabeledValue,
  ListState,
  ListToolbar,
  PageHeader,
  PageSizeSelect,
  Pagination,
  SearchInput,
  Select,
  Spinner,
  TOOLBAR_SELECT_CLASS,
} from '@/shared/ui';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useState, type ReactNode } from 'react';
import { PrescriptionDownloadDialog } from './PrescriptionDownloadDialog';
import {
  PrescriptionsFilterDrawer,
  type PrescriptionsFilter,
} from './PrescriptionsFilterDrawer';
import type { PrescriptionsSearch } from './prescriptionsSearch';
import { usePrescriptionDownloads } from './usePrescriptionDownloads';

const routeApi = getRouteApi('/partner/prescriptions');

const toPeriodKey = (period: RegistrationPeriodOption) =>
  `${period.registrationYearMonth}:${period.settlementYearMonth ?? ''}`;

/**
 * URL 의 (등록 월, 정산 월)을 선택지에서 찾는다.
 * 정확한 조합 → 같은 등록 월의 첫 조합(05 업체에서 등록 월만 넘어온 경우) → 그 해의 기본값 순.
 */
const resolvePeriod = (
  options: RegistrationPeriodOption[],
  defaultOption: RegistrationPeriodOption | undefined,
  month: string | undefined,
  settlementMonth: string | undefined,
) =>
  options.find(
    (option) =>
      option.registrationYearMonth === month &&
      (option.settlementYearMonth ?? undefined) === settlementMonth,
  ) ??
  options.find((option) => option.registrationYearMonth === month) ??
  defaultOption;

/** 정렬 (RDL-113) — 기본 병의원명순 */
const SORT_OPTIONS: { value: PrescriptionRegistrationSort; label: string }[] = [
  { value: 'CLINIC_NAME', label: '병의원명순' },
  { value: 'CHECK_NEEDED', label: '확인 필요순' },
  { value: 'COMPANY_NAME', label: '담당자순' },
  { value: 'IMAGE_COUNT_DESC', label: '등록 파일 많은순' },
];
const DEFAULT_SORT: PrescriptionRegistrationSort = 'CLINIC_NAME';

const isPrescriptionSort = (
  value: string | undefined,
): value is PrescriptionRegistrationSort =>
  SORT_OPTIONS.some((option) => option.value === value);

/**
 * 07 처방 등록 현황 — 담당자 × 거래 병의원 × 처방 등록 월 기준. 조회 전용.
 * 등록 여부(병의원 수)와 확인 현황(장)을 분리해 보여 준다. 카드 클릭 → 07-1 상세 패널.
 */
export function PrescriptionStatusPage() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const page = search.page ?? 1;
  const pageSize = search.size ?? DEFAULT_VIEW_PAGE_SIZE;
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [isDownloadOpen, setDownloadOpen] = useState(false);
  // 압축은 모달을 닫아도 이어진다 — 화면에 둔다
  const downloads = usePrescriptionDownloads();

  // 1) 등록 년도 — URL year → 넘어온 등록 월의 년도 → 올해
  const currentMonth = getCurrentRegistrationMonth();
  const yearOptions = getRegistrationYearOptions();
  const requestedYear = search.year ?? search.month?.slice(0, 4);
  const year =
    requestedYear && yearOptions.includes(requestedYear)
      ? requestedYear
      : yearOptions[0];
  const isThisYear = year === currentMonth.slice(0, 4);

  // 2) 그 해의 등록·정산 월 — 서버 /months?registrationYear 의 실제 조합(최신순).
  //    올해는 현재 월이 기본(조합이 아직 없으면 정산 월 없는 현재 월), 지난해는 첫 조합
  const monthsQuery = usePrescriptionRegistrationMonths(year);
  const { options: periods, defaultOption } = buildRegistrationPeriodOptions(
    monthsQuery.data ?? [],
    isThisYear ? currentMonth : undefined,
  );
  const period = monthsQuery.isSuccess
    ? resolvePeriod(
        periods,
        defaultOption,
        search.month,
        search.settlementMonth,
      )
    : undefined;
  const periodIndex = period ? periods.indexOf(period) : -1;
  const hasNoPeriods = monthsQuery.isSuccess && periods.length === 0;
  // 목록·요약에 보낼 년월 — 정산 월이 없으면(현재 월 등록 전체) 보내지 않는다
  const periodQuery = period && {
    registrationYearMonth: period.registrationYearMonth,
    settlementYearMonth: period.settlementYearMonth ?? undefined,
  };

  // 요약은 년월만 — 검색·필터·정렬이 바뀌어도 다시 부르지 않는다
  const summary = usePrescriptionRegistrationSummary(periodQuery);
  const list = usePrescriptionRegistrationList(
    {
      registrationYearMonth: periodQuery?.registrationYearMonth,
      settlementYearMonth: periodQuery?.settlementYearMonth,
      keyword: search.keyword,
      userIds: search.userIds,
      registrationStatus: search.registrationStatus,
      pendingOnly: search.pendingOnly,
      // 없으면 서버 기본값 CLINIC_NAME(병의원명순)
      sort: search.sort,
      page: page - 1,
      size: pageSize,
    },
    // 년월 조합이 정해진 뒤에만 부른다
    { enabled: period !== undefined },
  );

  const filter: PrescriptionsFilter = {
    userIds: search.userIds,
    registrationStatus: search.registrationStatus,
  };
  const isFilterActive = Object.values(filter).some(
    (value) => value !== undefined,
  );

  const updateSearch = (patch: Partial<PrescriptionsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: undefined }) });

  const changeYear = (next: string | undefined) => {
    if (!next) return;
    // 올해는 기본값이라 URL에 남기지 않는다. 년도가 바뀌면 그 해의 기본 조합으로
    updateSearch({
      year: next === yearOptions[0] ? undefined : next,
      month: undefined,
      settlementMonth: undefined,
    });
  };

  const changePeriod = (next: RegistrationPeriodOption | undefined) => {
    if (!next) return;
    // 그 해의 기본값은 URL에 남기지 않는다. 바뀌면 1페이지로
    const isDefault = next === defaultOption;
    updateSearch({
      month: isDefault ? undefined : next.registrationYearMonth,
      settlementMonth: isDefault
        ? undefined
        : (next.settlementYearMonth ?? undefined),
    });
  };

  const openDetail = (row: PrescriptionRegistration) =>
    navigate({
      search: (prev) => ({
        ...prev,
        detailUserId: row.userId,
        detailClinicId: row.clinicId,
      }),
    });

  const closeDetail = useCallback(
    () =>
      navigate({
        search: (prev) => ({
          ...prev,
          detailUserId: undefined,
          detailClinicId: undefined,
        }),
      }),
    [navigate],
  );

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <PageHeader
        title="처방 등록 현황"
        description="담당자별 거래 병의원의 월별 처방 내역 이미지 등록 현황을 확인할 수 있어요."
      />

      {/* 등록 년도 → 그 해의 등록·정산 월. ‹ 이전(과거) / › 다음(최신)은 그 해 안의 인접 조합 */}
      <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-2">
        <Select
          label="처방 등록 년도"
          showLabel={false}
          allLabel={null}
          value={year}
          options={yearOptions.map((option) => ({
            value: option,
            label: `${option}년`,
          }))}
          onChange={changeYear}
          className="w-[120px]"
        />
        <div className="flex min-w-0 items-center">
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="이전 달"
            disabled={periodIndex < 0 || periodIndex >= periods.length - 1}
            onClick={() => changePeriod(periods[periodIndex + 1])}
          >
            <Icon name="chevronLeft" size={24} />
          </Button>
          <Select
            label="처방 등록 월"
            showLabel={false}
            allLabel={
              monthsQuery.isPending
                ? '불러오는 중…'
                : hasNoPeriods
                  ? '조회할 년월 없음'
                  : null
            }
            disabled={!period}
            value={period && toPeriodKey(period)}
            options={periods.map((option) => ({
              value: toPeriodKey(option),
              label: formatRegistrationPeriod(option),
            }))}
            onChange={(key) =>
              changePeriod(
                periods.find((option) => toPeriodKey(option) === key),
              )
            }
            // 모바일에서 긴 기간 문구는 줄임표로 — 좌우 화살표는 그대로 보인다
            className="min-w-0 shrink"
          />
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="다음 달"
            disabled={periodIndex <= 0}
            onClick={() => changePeriod(periods[periodIndex - 1])}
          >
            <Icon name="chevronRight" size={24} />
          </Button>
        </div>
        {/* 고른 년월 조합 + 검색어로 이미지 ZIP · 확정 처방 엑셀을 받는다 */}
        <Button
          variant="secondary"
          size="md"
          className="ml-auto gap-2"
          disabled={!period}
          onClick={() => setDownloadOpen(true)}
        >
          {downloads.archive.isRunning && <Spinner size="sm" />}
          {downloads.archive.isRunning ? '이미지 압축 중' : '다운로드'}
        </Button>
      </div>

      <SummaryCards
        summary={summary.data}
        // 년월이 정해지기 전(선택지 불러오는 중)이나 요약을 불러오는 중
        isLoading={monthsQuery.isPending || summary.isLoading}
        search={search}
        onChange={updateSearch}
      />

      <div className="flex w-full flex-col gap-4">
        <ListToolbar
          // 이전 결과를 보여 주며 새 조건으로 다시 불러오는 중
          isRefreshing={list.isFetching && !list.isLoading}
          summary={
            summary.data && (
              <span className="text-body-14 font-medium whitespace-nowrap text-fg-secondary">
                등록률 {summary.data.registrationRate}% (
                {formatCount(summary.data.registeredClinicCount)} /{' '}
                {formatCount(summary.data.totalClinicCount)})
              </span>
            )
          }
          controls={
            <>
              <Select
                label="정렬"
                allLabel={null}
                value={search.sort ?? DEFAULT_SORT}
                options={SORT_OPTIONS}
                onChange={(sort) =>
                  updateSearch({
                    // 기본값은 URL에 남기지 않는다
                    sort:
                      isPrescriptionSort(sort) && sort !== DEFAULT_SORT
                        ? sort
                        : undefined,
                  })
                }
                className={TOOLBAR_SELECT_CLASS}
              />
              <PageSizeSelect
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                onChange={(size) =>
                  updateSearch({
                    size: size === DEFAULT_VIEW_PAGE_SIZE ? undefined : size,
                  })
                }
              />
              <FilterButton
                active={isFilterActive}
                onClick={() => setFilterOpen(true)}
              />
            </>
          }
          search={
            <SearchInput
              value={search.keyword}
              placeholder="병의원명, 요양기관번호"
              onSearch={(keyword) => updateSearch({ keyword })}
            />
          }
        />

        <ListState
          isLoading={monthsQuery.isPending || list.isLoading}
          error={monthsQuery.error ?? list.error}
          isEmpty={hasNoPeriods || list.data?.content.length === 0}
          emptyMessage={
            hasNoPeriods
              ? `${year}년에 조회할 수 있는 처방 등록 년월이 없어요.`
              : undefined
          }
        />
        <ul className="flex w-full flex-col gap-2">
          {list.data?.content.map((row) => (
            <li key={`${row.userId}:${row.clinicId}`}>
              <RegistrationCard
                row={row}
                onOpen={() => openDetail(row)}
                onSelectUser={() => updateSearch({ userIds: [row.userId] })}
              />
            </li>
          ))}
        </ul>
      </div>

      {list.data && list.data.totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={list.data.totalCount}
          onChange={(nextPage) =>
            navigate({ search: (prev) => ({ ...prev, page: nextPage }) })
          }
        />
      )}

      <PrescriptionsFilterDrawer
        open={isFilterOpen}
        value={filter}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => {
          updateSearch(next);
          setFilterOpen(false);
        }}
      />

      {period && (
        <PrescriptionDownloadDialog
          open={isDownloadOpen}
          onClose={() => setDownloadOpen(false)}
          period={period}
          // 같은 등록 월의 정산 월 조합 (최신순)
          settlementMonths={periods
            .filter(
              (option) =>
                option.registrationYearMonth === period.registrationYearMonth,
            )
            .flatMap((option) =>
              option.settlementYearMonth ? [option.settlementYearMonth] : [],
            )}
          keyword={search.keyword}
          imageCount={
            summary.data &&
            summary.data.pendingImageCount + summary.data.verifiedImageCount
          }
          downloads={downloads}
        />
      )}

      {/* 07에서 열면 선택한 처방 등록 월 행을 강조 */}
      <RegistrationDetailPanel
        params={
          search.detailUserId && search.detailClinicId
            ? {
                userId: search.detailUserId,
                clinicId: search.detailClinicId,
                // 상세는 등록 월 전체 집계(정산 월 필터 없음)
                registrationYearMonth: period?.registrationYearMonth,
              }
            : null
        }
        onClose={closeDetail}
      />
    </div>
  );
}

interface RegistrationCardProps {
  row: PrescriptionRegistration;
  onOpen: () => void;
  /** 담당자 클릭 = 담당자 필터 (카드 클릭과 분리) */
  onSelectUser: () => void;
}

/** Figma card/처방등록 — 병의원 · 담당자 · 등록 상태/파일/확인 대기/확인 완료 */
function RegistrationCard({
  row,
  onOpen,
  onSelectUser,
}: RegistrationCardProps) {
  // 미등록 카드는 장수 대신 '-'
  const isRegistered = row.registrationStatus === 'REGISTERED';
  const imageCount = (value: number) =>
    isRegistered ? `${value}장` : <span className="text-fg-light">-</span>;

  return (
    <Card onPress={onOpen} className="flex-col gap-3 xl:flex-row xl:gap-0">
      {/* 병의원 열은 고정 폭 (거래처 정보와 같은 340px) */}
      <div className="flex w-full min-w-0 flex-col justify-center gap-2 xl:w-[340px] xl:shrink-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-subtitle-18 font-bold text-fg-primary">
            {orDash(row.clinicName)}
          </p>
          {row.clinicClosedOn && <Badge tone="error">폐업</Badge>}
        </div>
        <DotSeparated
          className="text-body-14 font-medium whitespace-nowrap text-fg-secondary"
          items={[orDash(row.ykiho), toSigungu(row.region)]}
        />
      </div>

      <LabeledValue
        label="담당자"
        className="w-full justify-center gap-1 self-stretch xl:w-auto"
      >
        <DotSeparated
          items={[
            <button
              key="company"
              type="button"
              className="cursor-pointer text-fg-accent hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                onSelectUser();
              }}
            >
              {orDash(row.userCompanyName)}
            </button>,
            orDash(row.userRepresentativeName),
          ]}
        />
      </LabeledValue>

      <div className="flex w-full items-center gap-4 self-stretch xl:w-[480px] xl:shrink-0">
        <LabeledValue label="등록 상태" className="gap-1">
          <RegistrationStatusBadge status={row.registrationStatus} />
        </LabeledValue>
        <LabeledValue label="등록 파일" className="gap-1">
          {imageCount(row.imageCount)}
        </LabeledValue>
        <LabeledValue
          label="확인 대기"
          className="gap-1"
          valueClassName="text-fg-warning"
        >
          {imageCount(row.pendingImageCount)}
        </LabeledValue>
        <LabeledValue
          label="확인 완료"
          className="gap-1"
          valueClassName="text-fg-success"
        >
          {imageCount(row.verifiedImageCount)}
        </LabeledValue>
      </div>
    </Card>
  );
}

interface SummaryCardsProps {
  summary: PrescriptionRegistrationSummary | undefined;
  /** 숫자 자리에 스피너 */
  isLoading: boolean;
  search: PrescriptionsSearch;
  onChange: (patch: Partial<PrescriptionsSearch>) => void;
}

/**
 * 상단 요약 — [등록 현황] 전체/등록/미등록 클릭 = 등록 상태 필터,
 * [확인 현황] 확인 대기 클릭 = 확인 대기 1장 이상 필터. 값은 검색 조건과 무관한 그 달 전체.
 */
function SummaryCards({
  summary,
  isLoading,
  search,
  onChange,
}: SummaryCardsProps) {
  // 불러오는 중이면 숫자 자리(30px)에 스피너
  const count = (value: number | undefined, unit = '') =>
    isLoading ? (
      <Spinner size="md" className="my-[3px] align-middle" />
    ) : value == null ? (
      '-'
    ) : (
      formatCount(value, unit)
    );

  return (
    <div className="flex w-full flex-col items-stretch gap-4 md:flex-row md:gap-6">
      <SummaryGroup title="등록 현황" caption="담당자별 병의원 기준">
        <SummaryStat
          value={count(summary?.totalClinicCount)}
          label={<StatLabel>전체 거래 병의원</StatLabel>}
          // 기본 상태라 강조하지 않는다 — 등록/미등록 필터 해제용
          onClick={() => onChange({ registrationStatus: undefined })}
        />
        <SummaryStat
          value={count(summary?.registeredClinicCount)}
          label={<StatusStatLabel status="REGISTERED" />}
          active={search.registrationStatus === 'REGISTERED'}
          onClick={() => onChange({ registrationStatus: 'REGISTERED' })}
        />
        <SummaryStat
          value={count(summary?.unregisteredClinicCount)}
          label={<StatusStatLabel status="UNREGISTERED" />}
          active={search.registrationStatus === 'UNREGISTERED'}
          onClick={() => onChange({ registrationStatus: 'UNREGISTERED' })}
        />
      </SummaryGroup>
      <SummaryGroup title="확인 현황" caption="등록 이미지 기준">
        <SummaryStat
          value={count(summary?.pendingImageCount, '장')}
          valueClassName="text-fg-warning"
          label={<StatLabel>확인 대기</StatLabel>}
          active={search.pendingOnly === true}
          // 다시 누르면 해제
          onClick={() =>
            onChange({ pendingOnly: search.pendingOnly ? undefined : true })
          }
        />
        <SummaryStat
          value={count(summary?.verifiedImageCount, '장')}
          valueClassName="text-fg-success"
          label={<StatLabel>확인 완료</StatLabel>}
        />
      </SummaryGroup>
    </div>
  );
}

function SummaryGroup({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="flex flex-1 flex-col gap-6 rounded-20 bg-surface-white p-5"
    >
      <h2 className="flex items-center gap-2 whitespace-nowrap">
        <span className="text-subtitle-16 font-bold text-fg-primary">
          {title}
        </span>
        <span className="text-label-14 font-medium text-fg-tertiary">
          {caption}
        </span>
      </h2>
      <div className="flex w-full items-start justify-center gap-6">
        {children}
      </div>
    </section>
  );
}

/** Figma 등록 현황 — [등록|미등록 배지] + '병의원' (간격 4px) */
function StatusStatLabel({ status }: { status: RegistrationStatus }) {
  return (
    <span className="flex min-h-8 items-center justify-center gap-1">
      <RegistrationStatusBadge status={status} size="md" />
      <span className="text-body-15 font-medium whitespace-nowrap text-fg-secondary">
        병의원
      </span>
    </span>
  );
}

function StatLabel({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-h-8 items-center text-center text-body-15 font-medium text-fg-secondary">
      {children}
    </span>
  );
}

interface SummaryStatProps {
  value: ReactNode;
  label: ReactNode;
  valueClassName?: string;
  active?: boolean;
  /** 없으면 누를 수 없는 항목 */
  onClick?: () => void;
}

function SummaryStat({
  value,
  label,
  valueClassName,
  active,
  onClick,
}: SummaryStatProps) {
  return (
    <button
      type="button"
      disabled={!onClick}
      aria-pressed={onClick ? active : undefined}
      onClick={onClick}
      className={cn(
        // 호버·선택 배경용 여백은 -my 로 상쇄해 Figma 높이(158)를 유지한다
        'flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-16 -my-2 py-2 disabled:cursor-default',
        onClick && 'hover:bg-surface-grey',
        active && 'bg-surface-grey',
      )}
    >
      <span
        className={cn(
          'text-heading-24 font-bold text-fg-primary',
          valueClassName,
        )}
      >
        {value}
      </span>
      {label}
    </button>
  );
}
